import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import PillarPageLayout from "./PillarPageLayout";
import AuthorByline from "@/components/AuthorByline";

const FAQ = [
  {
    q: "¿Qué es el glutatión y para qué sirve?",
    a: "El glutatión (GSH) es un tripéptido formado por cisteína, glutamina y glicina. Es el antioxidante más abundante en cada célula del cuerpo y se considera el 'antioxidante maestro' porque neutraliza radicales libres, elimina toxinas, regenera otros antioxidantes (como las vitaminas C y E) y es esencial para el funcionamiento del sistema inmune.",
  },
  {
    q: "¿Cómo puedo aumentar el glutatión naturalmente?",
    a: "Tres vías comprobadas: (1) consumir precursores naturales como Immunocal (proteína aislada de suero), (2) comer alimentos ricos en azufre como ajo, cebolla, brócoli, col rizada y huevo, y (3) reducir el estrés oxidativo evitando tabaco, alcohol excesivo, contaminación y manteniendo sueño de calidad.",
  },
  {
    q: "¿Por qué disminuye el glutatión con la edad?",
    a: "A partir de los 30-40 años, los niveles intracelulares de glutatión bajan ~10% por década. Esto se debe a menor producción endógena, mayor exposición acumulada a tóxicos y declive de las enzimas que reciclan el GSH oxidado de vuelta a su forma activa.",
  },
  {
    q: "¿Suplementar glutatión oral es lo mismo que aumentar precursores?",
    a: "No. El glutatión oral se degrada en el estómago antes de llegar a las células. Los precursores como Immunocal (whey isolate no desnaturalizado) son más eficientes porque permiten que cada célula sintetice su propio GSH internamente.",
  },
  {
    q: "¿Qué relación tiene el glutatión con el sistema inmune?",
    a: "El glutatión es CRÍTICO para los linfocitos T (células T-helper y T-citotóxicas). Niveles bajos de GSH se correlacionan con menor función inmune. Por eso elevar el glutatión apoya la defensa frente a infecciones, recuperación post-enfermedad y la inmunidad en general.",
  },
];

const RELATED = [
  { label: "Immunocal: guía completa", href: "/immunocal", desc: "El precursor de glutatión más estudiado y patentado del mundo." },
  { label: "Cómo fortalecer tu sistema inmunológico", href: "/sistema-inmunologico", desc: "Estrategias funcionales basadas en evidencia." },
  { label: "Blog Eliora", href: "/blog", desc: "Más artículos sobre biohacking y medicina funcional." },
];

export default function GlutationPage(props) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <Helmet>
        <title>Glutatión: el antioxidante maestro [Beneficios y cómo aumentarlo]</title>
        <meta name="description" content="Qué es el glutatión, por qué es el antioxidante maestro, cómo aumentarlo naturalmente y su relación con el sistema inmunológico. Guía científica completa." />
        <link rel="canonical" href="https://elioralongevity.com/glutation" />
        <meta property="og:title" content="Glutatión: el antioxidante maestro [Beneficios y cómo aumentarlo]" />
        <meta property="og:description" content="Guía científica del glutatión, el antioxidante maestro: beneficios, cómo aumentarlo y su rol en la inmunidad." />
        <meta property="og:url" content="https://elioralongevity.com/glutation" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        })}</script>
      </Helmet>

      <PillarPageLayout
        {...props}
        meta={{
          eyebrow: "Salud celular · Antioxidantes",
          h1: "Glutatión: el antioxidante maestro que tu cuerpo produce",
          lead: "Conocer el glutatión es entender por qué algunas personas envejecen mejor, se enferman menos y se recuperan más rápido. Esta es la guía completa.",
        }}
        breadcrumbs={[{ label: "Inicio", href: "/" }, { label: "Glutatión" }]}
        faq={FAQ}
        relatedLinks={RELATED}
      >
        <AuthorByline date="2026-05-30" minutes={9} />

        <h2 className="font-serif text-3xl mt-2 mb-4">¿Qué es el glutatión (GSH)?</h2>
        <p className="text-[#1a2421] leading-relaxed mb-5">
          El <strong>glutatión</strong> (también llamado <em>GSH</em>, por <em>Glutathione</em>) es un tripéptido — una pequeña proteína formada por <strong>tres aminoácidos</strong>: cisteína, glutamina y glicina. Está presente en cada célula del cuerpo y es el <strong>antioxidante más abundante</strong> del organismo.
        </p>
        <p className="text-[#1a2421] leading-relaxed mb-8">
          Se le llama "el antioxidante maestro" porque no solo neutraliza radicales libres por sí mismo, sino que <strong>regenera</strong> a otros antioxidantes como las vitaminas C y E, y participa en la <strong>detoxificación hepática</strong> (Fase II) eliminando metales pesados y xenobióticos.
        </p>

        <h2 className="font-serif text-3xl mt-10 mb-4">Funciones críticas del glutatión</h2>
        <ul className="list-disc pl-6 space-y-3 mb-8 text-[#1a2421]">
          <li><strong>Defensa antioxidante:</strong> neutraliza radicales libres y especies reactivas de oxígeno (ROS).</li>
          <li><strong>Sistema inmune:</strong> esencial para la proliferación y función de los linfocitos T.</li>
          <li><strong>Detoxificación:</strong> conjuga toxinas en el hígado para su excreción.</li>
          <li><strong>Función mitocondrial:</strong> protege las mitocondrias del daño oxidativo, manteniendo la producción de energía celular.</li>
          <li><strong>Síntesis de ADN:</strong> participa en la replicación y reparación del ADN.</li>
          <li><strong>Regulación de la inflamación:</strong> modula respuestas inflamatorias crónicas.</li>
        </ul>

        <h2 className="font-serif text-3xl mt-10 mb-4">¿Por qué los niveles de glutatión bajan?</h2>
        <p className="text-[#1a2421] leading-relaxed mb-4">Hay tres causas principales:</p>
        <ol className="list-decimal pl-6 space-y-2 mb-8 text-[#1a2421]">
          <li><strong>Envejecimiento</strong> — los niveles intracelulares de GSH disminuyen ~10% por década después de los 30 años.</li>
          <li><strong>Estrés oxidativo crónico</strong> — contaminación, tabaco, alcohol, mala alimentación, sueño insuficiente y ejercicio extremo agotan las reservas.</li>
          <li><strong>Déficit de precursores</strong> — sin suficiente cisteína biodisponible, el cuerpo no puede sintetizar GSH.</li>
        </ol>

        <h2 className="font-serif text-3xl mt-10 mb-4">Cómo aumentar el glutatión naturalmente</h2>
        <p className="text-[#1a2421] leading-relaxed mb-4">Estas son las estrategias con mejor evidencia científica:</p>
        <ul className="list-disc pl-6 space-y-2 mb-8 text-[#1a2421]">
          <li><strong>Precursores especializados:</strong> <a href="/immunocal" className="text-[#bc6c25] font-semibold underline underline-offset-4">Immunocal</a> aporta cisteína biodisponible en forma de proteína aislada de suero no desnaturalizada.</li>
          <li><strong>Alimentos ricos en azufre:</strong> ajo, cebolla, brócoli, coliflor, col rizada, huevo.</li>
          <li><strong>Selenio y vitamina C:</strong> selenio (semillas de Brasil) y vitamina C apoyan al sistema GSH.</li>
          <li><strong>Sueño profundo:</strong> el glutatión se regenera durante el sueño REM.</li>
          <li><strong>Ejercicio moderado:</strong> aumenta la producción de glutatión endógeno (el extremo agota).</li>
          <li><strong>Reducir tóxicos:</strong> evitar tabaco, limitar alcohol, filtrar agua, reducir azúcar refinada.</li>
        </ul>

        <h2 className="font-serif text-3xl mt-10 mb-4">Glutatión y longevidad</h2>
        <p className="text-[#1a2421] leading-relaxed mb-8">
          Estudios en centenarios muestran que mantienen <strong>niveles de glutatión mucho más altos</strong> que la población general de su edad. El glutatión está siendo investigado activamente como un <em>marcador biológico de la edad</em> (más confiable que la edad cronológica) por su papel central en la salud mitocondrial, inmune y antioxidante.
        </p>

        <div className="not-prose bg-[#f9f6f0] border border-[#2c4a3b]/10 rounded-2xl p-5 my-6 text-sm">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold mb-2">Referencias científicas</p>
          <ul className="space-y-2 text-[#5c6b61]">
            <li>• Pizzorno J. "Glutathione!" <em>Integr Med (Encinitas)</em>. 2014. <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4684116/" target="_blank" rel="noopener noreferrer" className="text-[#bc6c25] hover:underline">NIH PMC</a></li>
            <li>• Lang CA et al. "Glutathione levels in healthy aging." <em>J Lab Clin Med</em>. 1992. <a href="https://pubmed.ncbi.nlm.nih.gov/1583390/" target="_blank" rel="noopener noreferrer" className="text-[#bc6c25] hover:underline">PubMed</a></li>
            <li>• Sekhar RV et al. "Glycine and N-acetylcysteine boost glutathione and improve aging-associated defects." <em>Clin Transl Med</em>. 2021. <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC8054190/" target="_blank" rel="noopener noreferrer" className="text-[#bc6c25] hover:underline">NIH PMC</a></li>
          </ul>
        </div>
      </PillarPageLayout>
    </>
  );
}
